import { twMerge } from "tailwind-merge";
import { Button } from "../Button";

export type Props = {
  /** Primary action button config (e.g., Save) */
  primaryAction: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    visuallyDisabled?: boolean;
    isLoading?: boolean;
  };
  /** Secondary action button config (e.g., Cancel) - optional */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  /** Additional CSS classes */
  className?: string;
};

/**
 * Sticky action bar that displays primary/secondary actions at the bottom of the viewport.
 * Positioned above the footer, with consistent width/padding matching page content.
 */
export function StickyActionBar({
  primaryAction,
  secondaryAction,
  className,
}: Props) {
  return (
    <div
      className={twMerge(
        "sticky bottom-0 left-0 right-0 z-50 w-full bg-brand-50/90 border-t border-brand-100 py-4",
        className
      )}
      data-testid="sticky-action-bar"
    >
      <div className="flex flex-row gap-2 md:gap-4 justify-end max-w-3xl w-full mx-auto pl-4 md:px-8">
        {secondaryAction && (
          <Button
            variant="secondary"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            data-testid="sticky-action-bar-secondary"
            className="min-w-24 md:w-auto"
          >
            {secondaryAction.label}
          </Button>
        )}
        <Button
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          visuallyDisabled={primaryAction.visuallyDisabled}
          isLoading={primaryAction.isLoading}
          data-testid="sticky-action-bar-primary"
          className="min-w-24 md:w-auto"
        >
          {primaryAction.label}
        </Button>
      </div>
    </div>
  );
}

export default StickyActionBar;
